import { ITreeNodeInstance } from '@infotech-soe/merps/types/Tree';
import { CategoryName, TDatabaseId } from '@infotech-soe/metaschema';
import cloneDeep from 'lodash/cloneDeep';

import { AppDispatch } from '../..';
import {
  CategoryTreeStateEntity,
  ICategoryTreeNode,
} from '../../../models/category/categoryTree';
import { RefreshStrategy } from '../../../services/commands/wrap/CategoryCommand';
import CatalogsService from '../../../services/dm-trees/CatalogsService';
import ICatalogsService from '../../../services/dm-trees/ICatalogsService';
import { TreeFormFieldNames } from '../../../services/form/schema-factory/TreeFormSchemaFactory';
import { FormSourceType } from '../../../services/form/view-model/FormSource';
import UniqueId from '../../../services/uid/UniqueId';
import { AppState } from '../../reducers/root';
import {
  getCatalogsStateEntity,
  isCatalogConnectedWithForm,
} from '../../selectors/catalogs';
import {
  getCategoryState,
  getPendingLoadConfig,
} from '../../selectors/category';
import { filterDatasetsForCategory } from '../../selectors/dataset';
import { getDictionaryById } from '../../selectors/dictionary';
import { getFormState } from '../../selectors/form';
import catalogs from '../../slices/catalogs';
import categoryActions, { CategoryTargetingInfo } from '../../slices/category';
import dataset from '../../slices/dataset';
import form from '../../slices/form';
import {
  apiCall,
  createAppListener,
  postDispatch,
  postDispatchAny,
} from '../listenerHelpers';

const catalogsListener = createAppListener();

const catalogsService: ICatalogsService = new CatalogsService();

const getTreeState = (
  state: AppState,
  catalogId: UniqueId
): CategoryTreeStateEntity => getCatalogsStateEntity(state, catalogId);

const getCategoryName = (
  state: AppState,
  catalogId: UniqueId
): CategoryName => {
  const { categoryId } = getTreeState(state, catalogId);
  const { source } = getCategoryState(state, categoryId);
  return source.category;
};

const findNode = (
  state: AppState,
  catalogId: UniqueId,
  nodeId: TDatabaseId
): ICategoryTreeNode => {
  const tree = getTreeState(state, catalogId);
  const node = tree.nodes.find(n => n.id === nodeId);
  if (!node) {
    throw new Error(`Node ${nodeId} does not exist`);
  }
  return node;
};

const nodeHasChildren = (
  state: AppState,
  catalogId: UniqueId,
  nodeId: TDatabaseId
): boolean => findNode(state, catalogId, nodeId).hasChildren;

const childrenAreLoaded = (
  state: AppState,
  catalogId: UniqueId,
  nodeId: TDatabaseId
): boolean => getTreeState(state, catalogId).childrenCache.has(nodeId);

const shouldLoadChildren = (
  state: AppState,
  catalogId: UniqueId,
  nodeId: TDatabaseId
): boolean =>
  nodeHasChildren(state, catalogId, nodeId) &&
  !childrenAreLoaded(state, catalogId, nodeId);

const getActiveNodeId = (
  state: AppState,
  catalogId: UniqueId
): TDatabaseId | null => getTreeState(state, catalogId).activeId;

const updateFormActiveNode = (
  catalogId: UniqueId,
  state: AppState,
  dispatch: AppDispatch
): void => {
  const { formId, activeId } = getTreeState(state, catalogId);
  if (!formId) return;
  const { source } = getFormState(state, formId);
  if (source.type === FormSourceType.PREDEFINED) {
    dispatch(
      form.update({
        formId,
        field: TreeFormFieldNames.TARGET_ID,
        value: activeId,
      })
    );
  }
};
const getProvidedOrActiveNodeId = (
  state: AppState,
  catalogId: UniqueId,
  nodeId?: TDatabaseId
): TDatabaseId => {
  if (nodeId !== undefined) {
    return nodeId;
  }

  const activeId = getActiveNodeId(state, catalogId);

  if (activeId === null) {
    throw new Error('Invalid state');
  }

  return activeId;
};

const forEachRootDataset = (
  state: AppState,
  categoryId: UniqueId,
  callback: (datasetId: UniqueId) => void
): void => {
  const category = getCategoryState(state, categoryId);
  const loadedDatasets = filterDatasetsForCategory(state, categoryId);
  for (const ds of Object.values(category.datasets)) {
    if (loadedDatasets[ds.id] !== undefined && ds.parent === null) {
      callback(ds.id);
    }
  }
};

const updateRootDatasets = (
  state: AppState,
  dispatch: AppDispatch,
  categoryId: UniqueId,
  targetingInfo?: CategoryTargetingInfo,
  forceDOMFocus?: true
): void => {
  forEachRootDataset(state, categoryId, datasetId => {
    dispatch(dataset.updateCatalogContext({ datasetId }));
    dispatch(dataset.update({ datasetId, targetingInfo, forceDOMFocus }));
  });
};

const updateDatasetsCatalogContext = (
  state: AppState,
  dispatch: AppDispatch,
  categoryId: UniqueId
): void => {
  forEachRootDataset(state, categoryId, datasetId => {
    dispatch(dataset.updateCatalogContext({ datasetId }));
  });
};

const isInCheckMode = (state: AppState, catalogId: UniqueId): boolean => {
  const tree = getTreeState(state, catalogId);
  const hasChecks = tree.checkedNodes.size > 0;
  const hierarchy = tree.hierarchyRootId !== null;
  return hasChecks && !hierarchy;
};

const isInHierarchyMode = (state: AppState, catalogId: UniqueId): boolean =>
  getTreeState(state, catalogId).hierarchyRootId !== null;

const updateDatasetsAfterCatalogChange = (
  state: AppState,
  dispatch: AppDispatch,
  categoryId: UniqueId,
  targetingInfo?: CategoryTargetingInfo,
  forceDOMFocus?: true
): void => {
  const inCheckMode = isInCheckMode(state, categoryId);
  const inHierarchyMode = isInHierarchyMode(state, categoryId);
  if (!inCheckMode && !inHierarchyMode) {
    updateRootDatasets(
      state,
      dispatch,
      categoryId,
      targetingInfo,
      forceDOMFocus
    );
  } else {
    updateDatasetsCatalogContext(state, dispatch, categoryId);
  }
};

const expandPathsToNodes = async (
  getState: () => AppState,
  dispatch: AppDispatch,
  catalogId: UniqueId,
  nodeIds: TDatabaseId[]
): Promise<void> => {
  const categoryName = getCategoryName(getState(), catalogId);
  for (const nodeId of nodeIds) {
    try {
      const pathIds = await catalogsService.getPath(categoryName, nodeId);
      for (const parentId of pathIds) {
        if (shouldLoadChildren(getState(), catalogId, parentId)) {
          const children = await catalogsService.loadChildren(
            categoryName,
            parentId
          );
          dispatch(
            catalogs.loadChildrenSuccess({
              catalogId,
              parentNodeId: parentId,
              nodes: children,
            })
          );
        }
        dispatch(catalogs.expandNode({ catalogId, nodeId: parentId }));
      }
    } catch {
      // Ignore errors for individual nodes
    }
  }
};

catalogsListener.startListening(
  apiCall(
    {
      request: catalogs.loadRoots,
      success: catalogs.loadRootsSuccess,
      failure: catalogs.loadRootsFailure,
    },
    async ({ targetingInfo, forceDOMFocus, catalogId }, state, dispatch) => {
      let resolvedTargetingInfo = targetingInfo;
      if (targetingInfo?.treePos) {
        const { category, value } = targetingInfo.treePos;
        const { categoryId } = getTreeState(state, catalogId);
        try {
          const treeId = await catalogsService.getCatalogIdByName(
            category,
            value
          );
          resolvedTargetingInfo = cloneDeep(targetingInfo);
          if (treeId) {
            delete resolvedTargetingInfo.treePos;
            resolvedTargetingInfo.relatedWithTree = {
              entityId: 0,
              treeId,
            };
          }
        } catch (err: any) {
          dispatch(categoryActions.close({ categoryId }));
          throw err;
        }
      }

      const categoryName = getCategoryName(state, catalogId);
      const rootNodes = await catalogsService.loadRoots(categoryName);

      return {
        catalogId,
        nodes: rootNodes,
        targetingInfo: resolvedTargetingInfo,
        forceDOMFocus,
      };
    }
  )
);

catalogsListener.startListening(
  postDispatch(
    catalogs.loadRootsSuccess,
    (
      { catalogId, nodes, targetingInfo, forceDOMFocus },
      state,
      dispatch,
      _prevState,
      getState
    ) => {
      if (nodes.length === 0) {
        return;
      }

      const { categoryId } = getTreeState(state, catalogId);
      const dictionary = getDictionaryById(state, categoryId);
      const { source } = getCategoryState(state, categoryId);
      const fullHierarchy =
        dictionary?.fullHierarchy || source.inParams?.linksGraph;

      const pendingLoad = getPendingLoadConfig(state, categoryId);
      const treeCheckedIds = pendingLoad?.treeCheckedIds;

      if (treeCheckedIds) {
        dispatch(
          catalogs.restoreChecks({ catalogId, nodeIds: treeCheckedIds })
        );
        const rootNodeIds = new Set(nodes.map(n => n.id));
        const nonRootCheckedIds = treeCheckedIds.filter(
          nodeId => !rootNodeIds.has(nodeId)
        );
        void expandPathsToNodes(
          getState,
          dispatch,
          catalogId,
          nonRootCheckedIds
        );
      }

      if (targetingInfo?.relatedWithTree?.treeId && !fullHierarchy) {
        dispatch(
          catalogs.locateNode({
            catalogId,
            nodeId: targetingInfo.relatedWithTree.entityId,
            treeId: targetingInfo.relatedWithTree.treeId,
            targetingInfo,
          })
        );
      } else {
        const firstNode = nodes[0];
        dispatch(
          catalogs.selectNode({
            catalogId,
            nodeId: firstNode.id,
            forceDOMFocus,
            fullHierarchy,
          })
        );
        if (firstNode.hasChildren) {
          dispatch(catalogs.expandNode({ catalogId, nodeId: firstNode.id }));
        }
        if (fullHierarchy) {
          dispatch(catalogs.setHierarchy({ catalogId }));
        }
      }

      if (treeCheckedIds) {
        updateRootDatasets(state, dispatch, categoryId, targetingInfo);
      }

      if (pendingLoad) {
        dispatch(categoryActions.clearPendingLoad({ categoryId }));
      }
    }
  )
);

catalogsListener.startListening(
  postDispatch(
    catalogs.expandNode,
    ({ catalogId, nodeId }, state, dispatch) => {
      const resolvedNodeId = getProvidedOrActiveNodeId(
        state,
        catalogId,
        nodeId
      );
      if (shouldLoadChildren(state, catalogId, resolvedNodeId)) {
        dispatch(
          catalogs.loadChildren({ catalogId, parentNodeId: resolvedNodeId })
        );
      }
    }
  )
);

catalogsListener.startListening(
  apiCall(
    {
      request: catalogs.loadChildren,
      success: catalogs.loadChildrenSuccess,
      failure: catalogs.loadChildrenFailure,
    },
    async ({ catalogId, parentNodeId }, state, dispatch) => {
      dispatch(catalogs.markNodePending({ catalogId, nodeId: parentNodeId }));
      const categoryName = getCategoryName(state, catalogId);
      const nodes = await catalogsService.loadChildren(
        categoryName,
        parentNodeId
      );
      return { catalogId, parentNodeId, nodes };
    }
  )
);

catalogsListener.startListening(
  postDispatch(
    catalogs.loadChildrenSuccess,
    ({ catalogId, parentNodeId }, _state, dispatch) => {
      dispatch(catalogs.unmarkNodePending({ catalogId, nodeId: parentNodeId }));
    }
  )
);

catalogsListener.startListening(
  postDispatchAny(
    [catalogs.selectNode, catalogs.nextNode, catalogs.prevNode],
    (
      payload: ReturnType<typeof catalogs.selectNode>['payload'],
      state,
      dispatch,
      prevState,
      type
    ) => {
      const { forceDOMFocus, fullHierarchy } =
        type === catalogs.selectNode.type
          ? payload
          : { forceDOMFocus: undefined, fullHierarchy: false };
      const { catalogId } = payload;
      const { formId, activeId, categoryId } = getTreeState(state, catalogId);

      if (formId) {
        const { source } = getFormState(state, formId);
        if (source.type === FormSourceType.PREDEFINED) {
          dispatch(
            form.update({
              formId,
              field: TreeFormFieldNames.TARGET_ID,
              value: activeId,
            })
          );
        }
      }

      const prevActiveId = getActiveNodeId(prevState, catalogId);
      const nextActiveId = getActiveNodeId(state, catalogId);
      const activeChanged = prevActiveId !== nextActiveId;

      if (
        activeChanged &&
        !isCatalogConnectedWithForm(state, catalogId) &&
        !fullHierarchy
      ) {
        const targeting =
          type === catalogs.selectNode.type ? payload.targetingInfo : undefined;
        updateDatasetsAfterCatalogChange(
          state,
          dispatch,
          categoryId,
          targeting,
          forceDOMFocus
        );
      }
    }
  )
);

catalogsListener.startListening(
  postDispatch(
    catalogs.collapseNode,
    ({ catalogId }, state, dispatch, prevState) => {
      const { categoryId } = getTreeState(state, catalogId);
      const prevActive = getActiveNodeId(prevState, catalogId);
      const nextActive = getActiveNodeId(state, categoryId);
      if (
        nextActive !== prevActive &&
        !isCatalogConnectedWithForm(state, catalogId)
      ) {
        updateRootDatasets(state, dispatch, categoryId);
      }
    }
  )
);

catalogsListener.startListening(
  postDispatch(catalogs.setHierarchy, ({ catalogId }, state, dispatch) => {
    updateRootDatasets(state, dispatch, catalogId);
  })
);

catalogsListener.startListening(
  apiCall(
    {
      request: catalogs.toggleCheck,
      success: catalogs.toggleCheckSuccess,
      failure: catalogs.toggleCheckFailure,
    },
    async ({ catalogId, nodeId, shift }, state, dispatch) => {
      const { categoryId } = getTreeState(state, catalogId);
      const categoryName = getCategoryName(state, catalogId);
      const id = getProvidedOrActiveNodeId(state, catalogId, nodeId);

      const nodeIds: TDatabaseId[] = shift
        ? await catalogsService.getRecursiveChildIds(categoryName, id)
        : [id];

      if (isInHierarchyMode(state, catalogId)) {
        dispatch(
          catalogs.hierarchyToChecks({ catalogId, excludedNodes: nodeIds })
        );
      } else {
        dispatch(
          catalogs.toggleNodeChecked({ catalogId, parentId: id, nodeIds })
        );
        updateRootDatasets(state, dispatch, categoryId);
      }

      return { catalogId, nodeId, shift };
    }
  )
);

catalogsListener.startListening(
  apiCall(
    {
      request: catalogs.locateNode,
      success: catalogs.locateNodeSuccess,
      failure: catalogs.locateNodeFailure,
    },
    async ({ catalogId, nodeId, treeId, targetingInfo }, state) => {
      const categoryName = getCategoryName(state, catalogId);
      const roots = await catalogsService.loadRoots(categoryName);
      const pathIds = await catalogsService.getPath(categoryName, treeId);
      const path: Array<[TDatabaseId, ITreeNodeInstance[]]> = [];
      for (const id of pathIds) {
        const nodes = await catalogsService.loadChildren(categoryName, id);
        path.push([id, nodes]);
      }
      return { catalogId, nodeId, roots, path, treeId, targetingInfo };
    }
  )
);

catalogsListener.startListening(
  postDispatch(
    catalogs.locateNodeSuccess,
    ({ catalogId, treeId, targetingInfo, path }, state, dispatch) => {
      const { nodes, refreshHierarchyRootId, refreshIsExpandedNode } =
        getTreeState(state, catalogId);

      const findNodeById = (
        nodeId?: TDatabaseId
      ): ICategoryTreeNode | undefined => nodes.find(({ id }) => id === nodeId);

      if (
        refreshHierarchyRootId !== null &&
        findNodeById(refreshHierarchyRootId)
      ) {
        dispatch(
          catalogs.setHierarchy({ catalogId, nodeId: refreshHierarchyRootId })
        );
      }

      const node = findNodeById(treeId);
      if (node) {
        dispatch(
          catalogs.selectNode({ catalogId, nodeId: treeId, targetingInfo })
        );
        if (refreshIsExpandedNode || node.parentId === null) {
          dispatch(catalogs.expandNode({ catalogId, nodeId: treeId }));
        }
      } else {
        const lvl = path.find(([currentTreeId]) => findNodeById(currentTreeId));
        if (lvl) {
          dispatch(
            catalogs.selectNode({ catalogId, nodeId: lvl[0], targetingInfo })
          );
        }
      }
    }
  )
);

catalogsListener.startListening(
  apiCall(
    {
      request: catalogs.refresh,
      success: catalogs.refreshSuccess,
      failure: catalogs.refreshFailure,
    },
    async ({ loadPathToNode, catalogId }, state, dispatch) => {
      const activeNodeId = loadPathToNode ?? getActiveNodeId(state, catalogId);
      const { categoryId, activeId, hierarchyRootId, expandedNodes } =
        getTreeState(state, catalogId);

      dispatch(catalogs.initializeTree({ catalogId: categoryId }));

      if (activeNodeId !== null && activeId !== null) {
        dispatch(
          catalogs.locateNode({
            catalogId: categoryId,
            nodeId: activeNodeId,
            treeId: activeId,
          })
        );
        return {
          catalogId,
          hierarchyRootId,
          isExpandedNode: expandedNodes.includes(activeNodeId),
        };
      } else {
        dispatch(catalogs.loadRoots({ catalogId: categoryId }));
      }

      return { catalogId };
    }
  )
);

catalogsListener.startListening(
  apiCall(
    {
      request: catalogs.hierarchyToChecks,
      success: catalogs.hierarchyToChecksSuccess,
      failure: catalogs.hierarchyToChecksFailure,
    },
    async ({ catalogId, excludedNodes }, state) => {
      const { hierarchyRootId } = getTreeState(state, catalogId);
      const categoryName = getCategoryName(state, catalogId);

      if (hierarchyRootId === null) {
        throw new Error('Not in hierarchy mode');
      }

      const childIds = await catalogsService.getRecursiveChildIds(
        categoryName,
        hierarchyRootId
      );
      const isExcludedInHierarchyList = childIds.some(id =>
        excludedNodes.includes(id)
      );
      const checkedNodes = isExcludedInHierarchyList
        ? childIds.filter(id => !excludedNodes.includes(id))
        : excludedNodes;

      return { catalogId, checkedNodes, excludedNodes };
    }
  )
);

catalogsListener.startListening(
  apiCall(
    {
      request: catalogs.create,
      success: catalogs.createSuccess,
      failure: catalogs.createFailure,
    },
    async ({ catalogId, parentNodeId, name }, state) => {
      const categoryName = getCategoryName(state, catalogId);
      const nodeId = await catalogsService.createCatalog(categoryName, {
        pid: parentNodeId,
        label: name,
      });
      return { catalogId, parentNodeId, nodeId, name };
    }
  )
);

catalogsListener.startListening(
  postDispatch(
    catalogs.createSuccess,
    ({ catalogId, parentNodeId }, state, dispatch, prevState) => {
      const shouldLoad = shouldLoadChildren(prevState, catalogId, parentNodeId);
      const { categoryId } = getTreeState(state, catalogId);
      updateFormActiveNode(catalogId, state, dispatch);
      updateRootDatasets(state, dispatch, categoryId);
      if (shouldLoad) {
        dispatch(catalogs.loadChildren({ catalogId, parentNodeId }));
      }
    }
  )
);

catalogsListener.startListening(
  apiCall(
    {
      request: catalogs.rename,
      success: catalogs.renameSuccess,
      failure: catalogs.renameFailure,
    },
    async ({ catalogId, nodeId, name }, state) => {
      const categoryName = getCategoryName(state, catalogId);
      await catalogsService.renameCatalog(categoryName, {
        id: nodeId,
        label: name,
      });
      return { catalogId, nodeId, name };
    }
  )
);

catalogsListener.startListening(
  postDispatch(catalogs.renameSuccess, ({ catalogId }, state, dispatch) => {
    updateFormActiveNode(catalogId, state, dispatch);
  })
);

catalogsListener.startListening(
  apiCall(
    {
      request: catalogs.delete,
      success: catalogs.deleteSuccess,
      failure: catalogs.deleteFailure,
    },
    async ({ catalogId, nodeId }, state) => {
      const categoryName = getCategoryName(state, catalogId);
      await catalogsService.deleteCatalog(categoryName, nodeId);
      return { catalogId, nodeId };
    }
  )
);

catalogsListener.startListening(
  postDispatch(catalogs.deleteSuccess, ({ catalogId }, state, dispatch) => {
    const { categoryId } = getTreeState(state, catalogId);
    updateFormActiveNode(catalogId, state, dispatch);
    updateRootDatasets(state, dispatch, categoryId);
  })
);

catalogsListener.startListening(
  apiCall(
    {
      request: catalogs.move,
      success: catalogs.moveSuccess,
      failure: catalogs.moveFailure,
    },
    async ({ catalogId, nodeId, newParentId }, state) => {
      const categoryName = getCategoryName(state, catalogId);
      await catalogsService.moveCatalog(categoryName, {
        id: nodeId,
        pid: newParentId,
      });
      return { catalogId, nodeId, newParentId };
    }
  )
);

catalogsListener.startListening(
  postDispatch(
    catalogs.moveSuccess,
    ({ catalogId, newParentId }, state, dispatch, prevState) => {
      const prevTree = getTreeState(prevState, catalogId);
      const shouldLoad = shouldLoadChildren(
        prevState,
        prevTree.categoryId,
        newParentId
      );
      const nextTree = getTreeState(state, catalogId);
      const activeChanged = prevTree.activeId !== nextTree.activeId;
      const hierarchyChanged =
        prevTree.hierarchyRootId !== nextTree.hierarchyRootId;

      if (activeChanged || hierarchyChanged) {
        updateRootDatasets(state, dispatch, prevTree.categoryId);
      }

      if (shouldLoad) {
        dispatch(
          catalogs.loadChildren({ catalogId, parentNodeId: newParentId })
        );
      }
    }
  )
);

catalogsListener.startListening(
  postDispatch(catalogs.clearChecks, ({ catalogId }, state, dispatch) => {
    const { categoryId } = getTreeState(state, catalogId);
    updateRootDatasets(state, dispatch, categoryId);
  })
);

catalogsListener.startListening(
  apiCall(
    {
      request: catalogs.moveIn,
      success: catalogs.moveInSuccess,
      failure: catalogs.moveInFailure,
    },
    async ({ catalogId, moveIds, newParentId }, state) => {
      const categoryName = getCategoryName(state, catalogId);
      for (const id of moveIds) {
        await catalogsService.moveInCatalog(categoryName, {
          id,
          pid: newParentId,
        });
      }
      return { catalogId, moveIds };
    }
  )
);

catalogsListener.startListening(
  postDispatch(
    catalogs.moveInSuccess,
    ({ catalogId, moveIds }, state, dispatch) => {
      const { categoryId } = getTreeState(state, catalogId);
      forEachRootDataset(state, categoryId, datasetId => {
        dispatch(
          dataset.refreshWithStrategy({
            datasetId,
            strategy: RefreshStrategy.REMOVE,
            ids: moveIds,
          })
        );
      });
    }
  )
);

export default catalogsListener.middleware;
